import React, {useMemo} from 'react';
import {Box, Tooltip, Typography} from '@mui/material';
import LockOpenOutlinedIcon from '@mui/icons-material/LockOpenOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import {grey} from '@mui/material/colors';
import moment from 'moment';

import {TWENTY_FOUR_HOUR_TIME_FORMAT} from '../../config/DateTimeConstants';
import {BOOKABLE_STATUS} from '../../helpers/TimeblockHelper';

const styles = {
    container: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        paddingBottom: '0.5rem',
        position: 'sticky',
        top: 0,
        zIndex: 10,
        backgroundColor: 'white',
        minWidth:'100%',
        width: '100%'
    },
    title: {
        fontWeight: 600,
        color: grey[900]
    },
    subtitle: {
        color: grey[700],
        marginTop: '0.125rem'
    },
    iconRow: {
        display: 'flex',
        gap: '0.5rem',
        alignItems: 'center',
        marginLeft: '1rem'
    },
    iconBox: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '0.25rem',
        backgroundColor: '#ecfdf3',
        border: '0.0625rem solid #16a34a',
        borderRadius: '0.25rem',
        padding: '0.2rem',
        height: '1.9rem',
        cursor: 'pointer'
    },
    iconBoxUnbookable: {
        backgroundColor: '#f3f4f6',
        border: '0.0625rem solid #9ca3af'
    },
    colorDot: {
        width: '0.5rem',
        height: '0.5rem',
        borderRadius: '50%'
    }
};

const ServiceBookabilityToggle = ({plan, isBookable, onToggle}) => {
    const bookableStatusLabel = isBookable ? BOOKABLE_STATUS.BOOKABLE : BOOKABLE_STATUS.UNBOOKABLE;

    return (
        <Tooltip title={`${plan.serviceType.keycode}: ${bookableStatusLabel}`} placement="top">
            <Box
                key={plan.id}
                sx={{
                    ...styles.iconBox,
                    ...(isBookable ? {} : styles.iconBoxUnbookable)
                }}
                onClick={onToggle}
                role="button"
                aria-label={`${plan.serviceType.keycode} ${bookableStatusLabel}`}>
                <Box sx={{...styles.colorDot, backgroundColor: plan.serviceType.primaryColor}}/>
                {
                    isBookable
                        ? <LockOpenOutlinedIcon fontSize="small" />
                        : <LockOutlinedIcon fontSize="small" />
                }
            </Box>
        </Tooltip>
    );
};

export default function ShiftPlanGridHeader({shiftPlan, bookableServiceTypes = [], setBookableServiceTypes}) {
    const serviceTypePlans = shiftPlan.bookableServiceTypePlans;

    const effectiveStatuses = useMemo(() => {
        if (!serviceTypePlans || serviceTypePlans.length === 0) {
            return [];
        }

        if (bookableServiceTypes.length === serviceTypePlans.length) {
            return bookableServiceTypes;
        }

        return serviceTypePlans.map(() => BOOKABLE_STATUS.BOOKABLE);
    }, [serviceTypePlans, bookableServiceTypes]);

    const shiftPlanTitle = useMemo(() => {
        const serviceTypeKeycodes = serviceTypePlans.map(plan => plan.serviceType.keycode);
        const shiftPlanDisplayIndex = shiftPlan.sequence;

        const shiftPlanLabel = `Shift Plan ${shiftPlanDisplayIndex}`;

        if (serviceTypeKeycodes.length === 0) {
            return shiftPlanLabel;
        }

        return `${shiftPlanLabel} - ${serviceTypeKeycodes.join(' + ')}`;
    }, [serviceTypePlans, shiftPlan]);

    const serviceTypeTimeRanges = useMemo(() => {
        return serviceTypePlans.map(plan => {
            const keycode = plan.serviceType.keycode;
            const fromTime = moment(plan.startTime).format(TWENTY_FOUR_HOUR_TIME_FORMAT);
            const toTime = moment(plan.endTime).format(TWENTY_FOUR_HOUR_TIME_FORMAT);

            return `${keycode}: ${fromTime}-${toTime}`;
        }).join(' · ');
    }, [serviceTypePlans]);

    const handleStatusChange = (index, value) => {
        if (!setBookableServiceTypes) {
            return;
        }

        const updatedStatuses = [...effectiveStatuses];
        updatedStatuses[index] = value;
        setBookableServiceTypes(updatedStatuses);
    };

    const toggleBookableStatus = (index) => {
        const nextValue = effectiveStatuses[index] === BOOKABLE_STATUS.UNBOOKABLE
            ? BOOKABLE_STATUS.BOOKABLE
            : BOOKABLE_STATUS.UNBOOKABLE;

        handleStatusChange(index, nextValue);
    };

    return (
        <Box sx={styles.container}>
            <Box>
                <Typography variant="subtitle1" sx={styles.title}>
                    {shiftPlanTitle}
                </Typography>
                <Typography variant="body2" sx={styles.subtitle}>
                    {serviceTypeTimeRanges}
                </Typography>
            </Box>
            <Box sx={styles.iconRow}>
                {serviceTypePlans.map((plan, index) => {
                    const isBookable = effectiveStatuses[index] !== BOOKABLE_STATUS.UNBOOKABLE;

                    return (
                    <ServiceBookabilityToggle
                        key={plan.id}
                        plan={plan}
                        isBookable={isBookable}
                        onToggle={() => toggleBookableStatus(index)}
                    />
                );})}
            </Box>
        </Box>
    );
}